/**
 * @fileoverview Helper utilities for formatting producto_inventario names with medida
 */

/**
 * Formats a producto_inventario name with medida concatenated
 * @param {Object} producto - Producto inventario object
 * @param {string} [producto.nombre] - Nombre del producto
 * @param {string} [producto.texto_medida] - Texto de medida del producto
 * @returns {string} Formatted string: "nombre (texto_medida)" or just "nombre" if medida is empty
 * 
 * @example
 * formatProductoNombre({ nombre: 'Papel Bond', texto_medida: 'kg' })
 * // Returns: 'Papel Bond (kg)'
 * 
 * @example
 * formatProductoNombre({ nombre: 'Papel Bond', texto_medida: '' })
 * // Returns: 'Papel Bond'
 */
export function formatProductoNombre(producto) {
  if (!producto) {
    return 'Sin nombre';
  }

  const nombre = producto.nombre || 'Sin nombre';
  const medida = producto.texto_medida?.trim();
  
  return medida ? `${nombre} (${medida})` : nombre;
}

/**
 * Formats a producto_inventario name with medida for Excel exports
 * Handles cases where nombre might be in expand.tinta.nombre
 * @param {Object} productoInv - Producto inventario object (may have expand)
 * @param {string} [productoInv.nombre] - Nombre del producto
 * @param {string} [productoInv.texto_medida] - Texto de medida del producto
 * @param {Object} [productoInv.expand] - Expanded relations
 * @param {Object} [productoInv.expand.tinta] - Tinta relation
 * @param {string} [productoInv.expand.tinta.nombre] - Nombre de la tinta
 * @returns {string} Formatted string: "nombre (texto_medida)" or just "nombre" if medida is empty, or "N/A" if no nombre available
 * 
 * @example
 * formatProductoNombreForExcel({ nombre: 'Papel Bond', texto_medida: 'kg' })
 * // Returns: 'Papel Bond (kg)'
 * 
 * @example
 * formatProductoNombreForExcel({ expand: { tinta: { nombre: 'Tinta Cyan' } }, texto_medida: 'litros' })
 * // Returns: 'Tinta Cyan (litros)'
 */
export function formatProductoNombreForExcel(productoInv) {
  if (!productoInv) {
    return 'N/A';
  }

  let nombre = 'N/A';
  
  // Prefer nombre over expand.tinta.nombre
  if (productoInv.nombre) {
    nombre = productoInv.nombre;
  } else if (productoInv.expand?.tinta?.nombre) {
    nombre = productoInv.expand.tinta.nombre;
  }
  
  // If nombre is still N/A, don't show medida
  if (nombre === 'N/A') {
    return 'N/A';
  }
  
  const medida = productoInv.texto_medida?.trim();
  
  return medida ? `${nombre} (${medida})` : nombre;
}

